import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingBag, X, Plus, Minus, Trash2, ArrowRight, Truck, Loader2 } from "lucide-react";
import { useCart } from "./CartContext";

const FREE_SHIP_THRESHOLD = 99900; // ₹999 in paise

export default function CartDrawer() {
  const { cart, drawerOpen, closeCart, removeFromCart, updateQuantity, total, itemCount, cartLoading } = useCart();
  const navigate = useNavigate();

  // Lock body scroll while open + close on Escape
  useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") closeCart(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [drawerOpen, closeCart]);

  const goCheckout = () => { closeCart(); navigate("/cart"); };
  const goStore    = () => { closeCart(); navigate("/store"); };

  const FREE_SHIPPING_IDS = [7];
  const allFreeShipping = cart.length > 0 && cart.every(
    i => i.product?.freeShipping || FREE_SHIPPING_IDS.includes(Number(i.product_id))
  );
  const shipping  = allFreeShipping || total >= FREE_SHIP_THRESHOLD || total === 0 ? 0 : 9900;
  const remaining = Math.max(0, FREE_SHIP_THRESHOLD - total);
  const shipPct   = Math.min(100, Math.round((total / FREE_SHIP_THRESHOLD) * 100));

  return (
    <>
      {/* Overlay */}
      <div
        onClick={closeCart}
        style={{
          position: "fixed", inset: 0, zIndex: 400,
          background: "rgba(0,0,0,0.5)",
          backdropFilter: "blur(2px)", WebkitBackdropFilter: "blur(2px)",
          opacity: drawerOpen ? 1 : 0,
          pointerEvents: drawerOpen ? "auto" : "none",
          transition: "opacity 0.3s ease",
        }}
      />

      {/* Drawer panel */}
      <aside
        role="dialog"
        aria-label="Shopping cart"
        style={{
          position: "fixed", top: 0, right: 0, bottom: 0, zIndex: 401,
          width: "min(420px, 100vw)",
          background: "var(--card-bg)",
          borderLeft: "1px solid var(--card-border)",
          boxShadow: "var(--shadow-xl)",
          display: "flex", flexDirection: "column",
          transform: drawerOpen ? "translateX(0)" : "translateX(105%)",
          transition: "transform 0.32s cubic-bezier(0.4,0,0.2,1)",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "1.1rem 1.25rem", borderBottom: "1px solid var(--card-border)", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ width: "34px", height: "34px", borderRadius: "10px", background: "var(--gold-faint)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ShoppingBag size={16} color="var(--gold)" />
            </div>
            <div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: "1.2rem", fontWeight: 700, color: "var(--ink)", lineHeight: 1 }}>Your Cart</div>
              <div style={{ fontSize: "0.72rem", color: "var(--muted)", marginTop: "2px" }}>{itemCount} item{itemCount !== 1 ? "s" : ""}</div>
            </div>
          </div>
          <button onClick={closeCart} aria-label="Close cart" style={{
            width: "34px", height: "34px", borderRadius: "50%", border: "1px solid var(--dust-2)",
            background: "none", color: "var(--muted)", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s",
          }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--gold)"; e.currentTarget.style.color = "var(--gold)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--dust-2)"; e.currentTarget.style.color = "var(--muted)"; }}
          ><X size={16} /></button>
        </div>

        {cartLoading ? (
          <div style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center", padding: "2rem" }}>
            <Loader2 size={34} color="var(--gold)" className="spin" />
          </div>
        ) : cart.length === 0 ? (
          /* Empty state */
          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "2rem", gap: "1rem" }}>
            <div style={{ width: "80px", height: "80px", borderRadius: "50%", background: "var(--gold-faint)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ShoppingBag size={34} color="var(--gold)" strokeWidth={1.5} />
            </div>
            <div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: "1.3rem", fontWeight: 700, color: "var(--ink)", marginBottom: "4px" }}>Your cart is empty</div>
              <p style={{ fontSize: "0.85rem", color: "var(--muted)", maxWidth: "260px", lineHeight: 1.6 }}>Browse our store to find beautiful hardcopy editions of Christian classics.</p>
            </div>
            <button onClick={goStore} style={{
              background: "linear-gradient(135deg, #3b82f6, #2563eb)",
              color: "white",
              border: "none",
              borderRadius: "9999px",
              padding: "12px 32px",
              fontSize: "15px",
              fontWeight: 600,
              cursor: "pointer"
            }}>Browse Store</button>
          </div>
        ) : (
          <>
            {/* Free-shipping progress */}
            <div style={{ padding: "0.9rem 1.25rem", borderBottom: "1px solid var(--card-border)", background: "var(--cream-2)", flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "7px", fontSize: "0.76rem", color: "var(--ink)", marginBottom: "7px" }}>
                <Truck size={13} color="var(--gold)" />
                {shipping === 0
                  ? <span style={{ fontWeight: 600 }}>You've unlocked <span style={{ color: "var(--sage)" }}>free shipping!</span></span>
                  : <span>Add <strong style={{ color: "var(--gold)" }}>₹{(remaining / 100).toFixed(0)}</strong> more for free shipping</span>
                }
              </div>
              <div style={{ height: "5px", background: "var(--dust)", borderRadius: "3px", overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${shipPct}%`, background: shipping === 0 ? "linear-gradient(90deg,var(--success),var(--success-2))" : "linear-gradient(90deg,var(--gold),var(--gold-light))", borderRadius: "3px", transition: "width 0.4s ease" }} />
              </div>
            </div>

            {/* Items */}
            <div className="no-scrollbar" style={{ flex: 1, overflowY: "auto", padding: "0.5rem 0" }}>
              {cart.map((item) => {
                const { product } = item;
                return (
                  <div key={item.product_id} style={{ display: "flex", gap: "12px", padding: "1rem 1.25rem", borderBottom: "1px solid var(--card-border)" }}>
                    {/* Cover */}
                    <div style={{
                      width: "56px", height: "78px", borderRadius: "var(--radius)", flexShrink: 0,
                      background: `linear-gradient(155deg, ${product.from || "#1C0F04"}, ${product.to || "#5A2E0A"})`,
                      display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.3rem",
                      boxShadow: "var(--shadow)", overflow: "hidden",
                    }}>
                      {product.cover_url
                        ? <img src={product.cover_url} alt={product.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        : "📖"}
                    </div>

                    {/* Details */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: "8px" }}>
                        <h4 style={{ fontFamily: "var(--font-display)", fontSize: "0.98rem", fontWeight: 700, color: "var(--ink)", lineHeight: 1.25, marginBottom: "2px" }}>{product.title}</h4>
                        <button onClick={() => removeFromCart(item.product_id)} aria-label="Remove" style={{ flexShrink: 0, width: "26px", height: "26px", borderRadius: "50%", border: "none", background: "none", color: "var(--muted)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s" }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = "var(--rust-faint)"; e.currentTarget.style.color = "var(--rust)"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "var(--muted)"; }}
                        ><Trash2 size={13} /></button>
                      </div>
                      <p style={{ fontSize: "0.76rem", color: "var(--muted)", marginBottom: "10px" }}>{product.author}</p>

                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
                        {/* Qty */}
                        <div style={{ display: "flex", alignItems: "center", gap: "6px", background: "var(--cream-2)", borderRadius: "var(--radius-full)", padding: "2px" }}>
                          <button onClick={() => updateQuantity(item.product_id, item.quantity - 1)} disabled={item.quantity <= 1} style={{ width: "24px", height: "24px", borderRadius: "50%", border: "none", background: item.quantity <= 1 ? "transparent" : "var(--card-bg)", color: item.quantity <= 1 ? "var(--muted)" : "var(--ink)", cursor: item.quantity <= 1 ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><Minus size={11} /></button>
                          <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "var(--ink)", minWidth: "18px", textAlign: "center" }}>{item.quantity}</span>
                          <button onClick={() => updateQuantity(item.product_id, item.quantity + 1)} style={{ width: "24px", height: "24px", borderRadius: "50%", border: "none", background: "var(--card-bg)", color: "var(--ink)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><Plus size={11} /></button>
                        </div>
                        <span style={{ fontFamily: "var(--font-display)", fontSize: "1.1rem", fontWeight: 700, color: "var(--ink)" }}>₹{((product.price * item.quantity) / 100).toFixed(0)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer / summary */}
            <div style={{ borderTop: "1px solid var(--card-border)", padding: "1.1rem 1.25rem", flexShrink: 0, background: "var(--card-bg)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                <span style={{ fontSize: "0.82rem", color: "var(--muted)" }}>Subtotal</span>
                <span style={{ fontSize: "0.88rem", fontWeight: 600, color: "var(--ink)" }}>₹{(total / 100).toFixed(0)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                <span style={{ fontSize: "0.82rem", color: "var(--muted)" }}>Shipping</span>
                <span style={{ fontSize: "0.82rem", fontWeight: 600, color: shipping === 0 ? "var(--sage)" : "var(--ink)" }}>{shipping === 0 ? "FREE" : `₹${(shipping / 100).toFixed(0)}`}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: "10px", borderTop: "1px solid var(--card-border)", marginBottom: "1rem" }}>
                <span style={{ fontWeight: 700, color: "var(--ink)" }}>Total</span>
                <span style={{ fontFamily: "var(--font-display)", fontSize: "1.5rem", fontWeight: 700, color: "var(--ink)" }}>₹{((total + shipping) / 100).toFixed(0)}</span>
              </div>

              <button onClick={goCheckout} style={{
                width: "100%", padding: "13px", borderRadius: "var(--radius-full)", border: "none",
                background: "linear-gradient(135deg,var(--gold),var(--gold-2))", color: "#fff",
                fontWeight: 700, fontSize: "0.92rem", cursor: "pointer", boxShadow: "var(--shadow-gold)",
                display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", transition: "all 0.2s",
              }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "var(--shadow-gold-lg)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "var(--shadow-gold)"; }}
              >
                Proceed to Checkout <ArrowRight size={15} />
              </button>
              <button onClick={goStore} style={{ width: "100%", marginTop: "8px", padding: "9px", background: "none", border: "none", color: "var(--muted)", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer" }}>
                Continue Shopping
              </button>
            </div>
          </>
        )}
      </aside>
    </>
  );
}
